import { useSelector } from '@bridebook/web/lib/utils';
import { ensureMotifPreviewFontLink } from '@bridebook/web/lib/wedding-website/motif/preview/ensure-preview-font-link';
import { getMotifPreviewOverride } from '@bridebook/web/lib/weddings/selectors';
import { CSSProperties, PropsWithChildren, ReactNode, useEffect } from 'react';

/**
 * Safety boundary for the Motif layout: the theme CSS is AI-generated and untrusted, so `.bb-ww-frame` (a
 * theme-contract-invisible element) locks down containment via inline style, which a theme can't override:
 * - `transform: translateZ(0)` (not `contain`/`isolation` — those broke the preview's scale measurement) makes
 *   the frame the containing block for `position: fixed` descendants and a stacking context, so a theme can't
 *   escape to the viewport or overlay app chrome via z-index.
 * - `overflow: clip` stops oversized/decorative content escaping sideways, without creating a scroll container.
 * - `clip-path: inset(0)` is a second, paint-only clip (no layout or containment effect) for the compositing
 *   cases where a promoted layer paints past `overflow: clip`.
 * - `container-type: inline-size` exposes the frame width to `cqw` without size containment.
 *
 * The frame has **no fixed height and isn't a scroll container** — it flows naturally and the surrounding
 * wrapper/document scrolls (avoids nested double-scrollbars). `--ww-height` resolves to the PREVIEWED device's
 * canvas height (via `--ww-canvas-height`, published by the preview wrapper), falling back to `100svh` for the
 * guest view where there's no wrapper and the real viewport is correct.
 *
 * The inner `.bb-ww` block is what the theme styles; `editorOverlay` sits beside it inside the frame,
 * out of the theme's reach.
 */
export const MotifContainer = ({
  children,
  editorOverlay,
}: PropsWithChildren<{ editorOverlay?: ReactNode }>) => {
  // Transient client-only override echo (suggestPalettes / suggestFonts tap): the core vars applied
  // INLINE on `.bb-ww` restyle the whole page instantly via the var contract — inline beats the linked
  // theme stylesheet, so there's no re-bake / refetch. Null (incl. guest view) ⇒ the served CSS wins.
  const override = useSelector(getMotifPreviewOverride);
  const palette = override?.palette;
  // Unlike colours, a font override's families must be LOADED before the vars point at them.
  const fonts = override?.fonts;
  useEffect(() => {
    ensureMotifPreviewFontLink(fonts?.importUrl);
  }, [fonts?.importUrl]);

  const overrideVars =
    palette || fonts
      ? ({
          ...(palette
            ? {
                '--ww-bg': palette.bg,
                '--ww-text': palette.text,
                '--ww-muted': palette.muted,
                '--ww-accent': palette.accent,
                '--ww-surface': palette.surface,
                '--ww-border': palette.border,
                '--ww-accent-strong': palette.accentStrong,
              }
            : {}),
          ...(fonts
            ? {
                '--ww-font-heading': fonts.heading,
                '--ww-font-body': fonts.body,
                ...(fonts.accent ? { '--ww-font-accent': fonts.accent } : {}),
              }
            : {}),
        } as CSSProperties)
      : undefined;

  return (
    <div
      className="bb-ww-frame"
      style={
        {
          position: 'relative',
          width: '100%',
          // Previewed-device canvas height (set by the preview wrapper); falls back to the real viewport in guest view.
          '--ww-height': 'var(--ww-canvas-height, 100svh)',
          overflow: 'clip',
          clipPath: 'inset(0)',
          transform: 'translateZ(0)',
          containerType: 'inline-size',
        } as CSSProperties
      }>
      <div className="bb-ww" style={overrideVars}>
        {/* Empty full-page decorative canvas (texture/wash/watermark) — hidden by default, themes opt in. */}
        <div className="bb-ww__backdrop" aria-hidden="true" />
        {children}
      </div>
      {editorOverlay}
    </div>
  );
};
